import React, { useCallback, useMemo } from "react";
import { dateUtils, planUtils, styleGenerators } from "../utils/calendarUtils";
import PlanDot from "./PlanDot";

const DateCell = React.memo(
  ({
    dateObj,
    line,
    lineIndex,
    dateIndex,
    dayWithDutyColumns,
    dropActiveIndex,
    droppingIndex,
    onPlanDoubleClick,
    dragDropProps,
    dragDropHandlers,
  }) => {
    const isDropActive = dropActiveIndex === `${lineIndex}-${dateIndex}`;
    const isDropping = droppingIndex === `${lineIndex}-${dateIndex}`;

    const startPlans = useMemo(
      () => planUtils.getStartPlans(dateObj.plans || []),
      [dateObj.plans],
    );

    const handleCellClick = useCallback(
      (e) => {
        // Only handle click if it's on the cell itself, not on a plan dot
        if (e.target === e.currentTarget) {
          // Prevent creation on fully planned days
          if (dateObj.is_full_day_planned) {
            console.log("Cannot create new plans on fully planned days");
            return;
          }

          // Prevent creation on holidays
          if (dateObj.week_status === "holiday") {
            console.log("Cannot create new plans on holidays");
            return;
          }

          // Create a mock plan object for new plan creation
          const mockPlan = {};
          onPlanDoubleClick(mockPlan, dateObj, line);
        }
      },
      [onPlanDoubleClick, dateObj, line],
    );

    const dragHandlers = useMemo(
      () => ({
        onDrop: (e) =>
          dragDropHandlers.handleDrop(e, {
            line_index: lineIndex,
            date_index: dateIndex,
            dateObj,
            ...dragDropProps,
          }),
        onDragOver: (e) =>
          dragDropHandlers.handleDragOver(e, {
            line_index: lineIndex,
            date_index: dateIndex,
            dateObj,
            ...dragDropProps,
          }),
        onDragLeave: (e) => dragDropHandlers.handleDragLeave(e, dragDropProps),
      }),
      [lineIndex, dateIndex, dateObj, dragDropProps, dragDropHandlers],
    );

    return (
      <div
        style={styleGenerators.getDateCellStyle(
          dayWithDutyColumns,
          dateObj,
          isDropActive,
          isDropping,
        )}
        onDoubleClick={handleCellClick}
        {...dragHandlers}
      >
        {startPlans.map((plan, planIndex) => (
          <PlanDot
            key={plan.id || planIndex}
            plan={plan}
            planIndex={planIndex}
            dateObj={dateObj}
            line={line}
            onDoubleClick={onPlanDoubleClick}
            isDropping={isDropping}
            dragProps={{
              onDragStart: (e) =>
                dragDropHandlers.handleDragStart(e, {
                  line_index: lineIndex,
                  date_index: dateIndex,
                  plan,
                  ...dragDropProps,
                }),
              onDragEnd: (e) =>
                dragDropHandlers.handleDragEnd(e, dragDropProps),
            }}
          />
        ))}

        {/* Show plus icon for empty clickable cells */}
        {startPlans.length === 0 &&
          !dateUtils.isPastDate(dateObj.date) &&
          dateObj.week_status !== "weekend" &&
          dateObj.week_status !== "holiday" &&
          !dateObj.is_full_day_planned && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                fontSize: "12px",
                color: "#bbb",
                pointerEvents: "none",
                opacity: 0.6,
              }}
            >
              +
            </div>
          )}

        {isDropping && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              fontSize: "10px",
              color: "#fff",
              fontWeight: "bold",
            }}
          >
            ⟳
          </div>
        )}
      </div>
    );
  },
);

DateCell.displayName = "DateCell";

export default DateCell;
